import { useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Truck, CheckCircle2 } from "lucide-react";
import { COMPANY, waLink } from "@/lib/constants";

const VIDEO_URL =
  "https://customer-assets-jai6qajn.emergentagent.net/job_guincho-24h-rp/artifacts/04510f191e1c8da4_WhatsApp%20Video%202026-10-28%20at%2015.23.14.mp4";

const BULLETS = [
  "Guincho pesado para caminhões, caminhonetes e utilitários",
  "Plataforma reforçada com alta capacidade de carga",
  "Equipe especializada em veículos de grande porte",
  "Operação 24h em Ribeirão Preto e região",
  "Viagens interestaduais para todo o Brasil",
  "Atendimento emergencial em rodovias e estradas",
];

export const HeavyVehicles = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <section
      id="pesados"
      data-testid="heavy-vehicles-section"
      className="section-pad bg-[#141414] relative overflow-hidden border-y border-[#2d3748]"
    >
      {/* Dramatic diagonal red accent */}
      <div className="absolute top-0 left-0 right-0 h-1 siren-stripe"></div>
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-[#d90429]/15 blur-3xl rounded-full pointer-events-none"></div>
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-[#d90429]/10 blur-3xl rounded-full pointer-events-none"></div>

      <div className="container-custom relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content side */}
          <div>
            <div className="inline-flex items-center gap-2 bg-[#d90429] px-4 py-2 mb-6">
              <Truck className="w-4 h-4 text-white" />
              <span className="overline text-white text-[10px]">
                Serviço Especializado
              </span>
            </div>

            <h2
              data-testid="heavy-headline"
              className="font-display uppercase font-bold text-white text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight"
            >
              Guincho para{" "}
              <span className="text-[#d90429]">veículos pesados</span>
              <br />& caminhões
            </h2>

            <p className="mt-6 text-gray-300 text-lg leading-relaxed max-w-xl">
              Operamos com <strong className="text-white">plataformas reforçadas</strong>{" "}
              e equipe treinada para remoção e transporte de caminhões,
              caminhonetes de grande porte, vans e utilitários pesados — com
              total segurança, em qualquer hora do dia ou da noite.
            </p>

            <ul className="mt-8 space-y-3">
              {BULLETS.map((b, i) => (
                <li
                  key={i}
                  data-testid={`heavy-bullet-${i}`}
                  className="flex items-start gap-3 text-gray-200"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#d90429] shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href={`tel:${COMPANY.phoneTel}`}
                data-testid="heavy-phone-cta"
                className="btn-red text-base md:text-lg"
              >
                <Truck className="w-5 h-5" />
                Ligar: {COMPANY.phoneDisplay}
              </a>
              <a
                href={waLink(
                  "Olá! Preciso de guincho para veículo pesado/caminhão em Ribeirão Preto.",
                )}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="heavy-whatsapp-cta"
                className="btn-wa text-base"
              >
                <i className="fa-brands fa-whatsapp text-xl" aria-hidden></i>
                Orçamento no WhatsApp
              </a>
            </div>
          </div>

          {/* Video side */}
          <div className="relative">
            <div className="absolute -inset-2 md:-inset-3 border-2 border-[#d90429]/40 -z-10"></div>
            <div className="relative bg-black border border-[#2d3748] overflow-hidden aspect-[4/5] md:aspect-video">
              <video
                ref={videoRef}
                data-testid="heavy-showcase-video"
                src={VIDEO_URL}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
              />

              {/* Top badge */}
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[#d90429] px-3 py-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                </span>
                <span className="overline text-white text-[10px]">
                  Operação Real
                </span>
              </div>

              {/* Controls */}
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={togglePlay}
                    data-testid="heavy-video-play-toggle"
                    aria-label={playing ? "Pausar vídeo" : "Reproduzir vídeo"}
                    className="bg-[#d90429] hover:bg-[#ef233c] text-white w-11 h-11 rounded-full flex items-center justify-center transition-colors"
                  >
                    {playing ? (
                      <Pause className="w-5 h-5" />
                    ) : (
                      <Play className="w-5 h-5 ml-0.5" />
                    )}
                  </button>
                  <button
                    onClick={toggleMute}
                    data-testid="heavy-video-mute-toggle"
                    aria-label={muted ? "Ativar som" : "Mutar"}
                    className="bg-black/70 hover:bg-black backdrop-blur text-white w-11 h-11 rounded-full flex items-center justify-center border border-white/20 transition-colors"
                  >
                    {muted ? (
                      <VolumeX className="w-5 h-5" />
                    ) : (
                      <Volume2 className="w-5 h-5" />
                    )}
                  </button>
                </div>

                <div className="hidden sm:flex flex-col items-end text-right">
                  <span className="overline text-white text-[10px]">
                    Ventania
                  </span>
                  <span className="font-display uppercase text-white font-bold text-sm leading-tight">
                    Pesados · 24h
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
